/** For chunks */
// eslint-disable-line
// import { hot } from 'react-hot-loader/root';

// IMPORTANT: these libs should be imported firstly
import 'regenerator-runtime/runtime'; // need to use async await

import React from 'react';

import { Capacitor } from '@capacitor/core';
import { EdgeToEdge } from '@capawesome/capacitor-android-edge-to-edge-support';
import { App } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';
import { Device } from '@capacitor/device';
import { toast } from 'react-toastify';
import { bindActionCreators } from 'redux';
import Q from 'q';

import picsioConfig from '../../../config';
import PubSubService from './services/PubSubService';
import Logger from './services/Logger';
import localization from './shared/strings';
import ua from './ua';
import * as utils from './shared/utils';
/** Store */
import store from './store';
import { search } from './store/keywords/actions';
import { get } from './store/actions/customFields';
import { get as getProductAttributes } from './store/actions/productAttributes';
import {
  selectAll,
  deselectAll,
  changeFlag,
  changeRating,
  changeColor,
  getWatermarks,
  selectAllByMatch,
} from './store/actions/assets';
import {
  toggleDetails,
  toggleImport,
  changeTree,
  setPanelSize,
  setIsWebpSupported,
} from './store/actions/main';
import { getLightboards } from './store/actions/lightboards';
import { getInboxes } from './store/inboxes/actions';
import { getFaces } from './store/faces/actions';
import { getNotifications, fetchJobsStatus } from './store/actions/notifications';
import { setRecursiveSearch, getCollections } from './store/actions/collections';
import { getProducts } from './store/products/actions';
import { getSavedSearches } from './store/savedSearches/actions';
import { addToDownloadList } from './store/actions/downloadList';
import { getUser, updateUser } from './store/actions/user';
import logout from './store/helpers/user/logout';
import isHaveTeammatePermission from './store/helpers/user/isHaveTeammatePermission';
import { getRoles } from './store/roles/actions';
import { getTeam } from './store/reducers/teammates';
import showDownloadDialog from './helpers/fileDownloader/showDownloadDialog';
import checkUserEventSubscription from './helpers/checkUserEventSubscription';
import { COLOR_LABELS } from './helpers/colorsHelper';
import { selectColorLabels } from './store/selectors/colorLabels';
import UiBlocker from './services/UiBlocker';
import BrowserNotifications from './services/BrowserNotifications';
import initMobileNotifications from './helpers/initMobileNotifications';
import isTwoFactorEnabled from './helpers/isTwoFactorEnabled';
import { setUserDateLocale } from './shared/dateLocale';
import { initShortcuts } from './components/Shortcuts';
import '../css/styles.scss';
import {
  navigate, replace, reloadApp, getPreviewId, isRoutePreview,
} from './helpers/history';
import { showDialog } from './components/dialog';
import checkBrowser from './helpers/checkBrowser';
import CONSTANTS from './shared/constants';
import { isUnsubscribed, isToriutTrialLocked } from './store/helpers/user';
import Toast from './components/Toast';
import sendEventToIntercom, {
  markIntercomEventSessionStarted,
  resetIntercomEventSession,
} from './services/IntercomEventService';
import Tag from './components/Tag/Tag';
import { formatDueDate, getSubscriptionEnd } from './shared/utils';

const webRoot = document.getElementById('webRoot');
/* eslint-disable-next-line */
__webpack_public_path__ = webRoot ? webRoot.textContent : null;

const getCustomFieldsAction = bindActionCreators({ get }, store.dispatch).get;
const getProductAttributesAction = bindActionCreators({ getProductAttributes }, store.dispatch).getProductAttributes;
const keywordsActions = bindActionCreators({ search }, store.dispatch);
const lightboardsActions = bindActionCreators({ getLightboards }, store.dispatch);
const inboxesActions = bindActionCreators({ getInboxes }, store.dispatch);
const facesActions = bindActionCreators({ getFaces }, store.dispatch);
const notificationsActions = bindActionCreators(
  { getNotifications, fetchJobsStatus },
  store.dispatch,
);
const collectionsActions = bindActionCreators(
  { getCollections, setRecursiveSearch },
  store.dispatch,
);

const productsActions = bindActionCreators({ getProducts }, store.dispatch);
const savedSearchesActions = bindActionCreators({ getSavedSearches }, store.dispatch);
const userActions = bindActionCreators({ getUser, updateUser }, store.dispatch);
const rolesActions = bindActionCreators({ getRoles }, store.dispatch);
const teamActions = bindActionCreators({ getTeam }, store.dispatch);

const assetsActions = bindActionCreators(
  {
    selectAll,
    deselectAll,
    changeFlag,
    changeRating,
    changeColor,
    getWatermarks,
    selectAllByMatch,
  },
  store.dispatch,
);
const mainActions = bindActionCreators(
  {
    toggleDetails, toggleImport, changeTree, setPanelSize, setIsWebpSupported,
  },
  store.dispatch,
);
const downloadListActions = bindActionCreators({ addToDownloadList }, store.dispatch);

function GetDataError(message, code, err) {
  this.name = 'GetDataError';
  this.message = message || 'Get data error';
  this.code = code || 'getDataError';
  this.err = err || null;
  this.stack = (new Error()).stack;
}
GetDataError.prototype = Object.create(Error.prototype);
GetDataError.prototype.constructor = GetDataError;

const app = {
  logout() {
    showDialog({
      title: localization.DIALOGS.LOGOUT_DIALOG.TITLE,
      text: localization.DIALOGS.LOGOUT_DIALOG.TEXT,
      textBtnCancel: localization.DIALOGS.LOGOUT_DIALOG.CANCEL_TEXT,
      textBtnOk: localization.DIALOGS.LOGOUT_DIALOG.OK_TEXT,
      onOk() {
        resetIntercomEventSession();
        window.Intercom('shutdown');

        const detailsPanelEditable = utils.LocalStorage.get('picsio.detailsPanelEditable') || {};
        if (detailsPanelEditable.unlockLogout === true) {
          detailsPanelEditable.unlockLogout = false;
          detailsPanelEditable.lockAlways = true;
          utils.LocalStorage.set('picsio.detailsPanelEditable', detailsPanelEditable);
        }

        utils.setCookie('picsio.user_id', '');

        logout(store.getState().user)
          .then((user) => {
            /** wait until send event to analytics */
            setTimeout(() => {
              UiBlocker.unblock();
              if (ua.isPWA()) {
                window.location = '/app';
              } else if (ua.isMobileApp()) {
                window.location = '/appmobile';
              } else {
                let customLoginPageUrl = '';
                if (user.loginPageUrl && user.loginPageEnable) {
                  customLoginPageUrl = user.loginPageUrl;
                } else if (user.team && user.team.loginPageUrl && user.team.loginPageEnable) {
                  customLoginPageUrl = user.team.loginPageUrl;
                }
                window.location = customLoginPageUrl ? `https://${customLoginPageUrl}` : '/';
              }
            }, 350);
          })
          .catch(console.error.bind(console));
      },
    });
  },

  /**
   * Upsert user at Intercom by opening session
   */
  startIntercomSession(user, detail) {
    const { customer } = user;
    const { subscriptionFeatures } = user;

    // user_id or email is required by Intercom
    const payload = {
      user_id: user._id,
      name: user.displayName,
      email: user.email,
      created_at: Math.floor(new Date(user.createdAt).getTime() / 1000), // Signup date as a Unix timestamp
    };

    let deviceType = 'desktop';
    if (ua.isMobileApp()) {
      const platform = ua.getPlatform();
      if (platform) {
        deviceType = `${utils.capitalizeFirstLetter(platform)}App`;
      }
    } else if (window.innerWidth < 1024) {
      deviceType = 'mobile';
    }

    payload.device_type = deviceType;

    // to avoid uses interlapping, enable user hashing
    if (store.getState().user.intercomHMAC) {
      payload.user_hash = user.intercomHMAC;
    }

    // add extra info to payload to see this in Intercom admin
    payload['working folder'] = user.workingFolderId;

    if (customer) {
      payload.stripe_id = customer && customer.id;
      const currentPlan = utils.getUserPlan(customer) || {};
      payload.stripe_plan = currentPlan.name;
    }

    if (subscriptionFeatures) {
      payload['teammates count'] = subscriptionFeatures.teammatesCount || 0;
      payload['websites count'] = subscriptionFeatures.websitesCount || 0;
    }

    if (detail?.message) {
      window.Intercom('showNewMessage', detail?.message);
    } else if (detail?.showNewMessage) {
      window.Intercom('showNewMessage');
    } else {
      window.Intercom('update', payload);
    }

    if (!store.getState().user.intercomUser) {
      userActions.updateUser({ intercomUser: true }, false);
    }
  },

  delegateGlobalEvents() {
    // enable Q long stack traces
    Q.longStackSupport = true;

    // when laptop wakes up and when connection was broken and then runs again
    const toastId = {};
    if (picsioConfig.isMainApp) {
      window.addEventListener('online', () => {
        Logger.info('window online');
        toast.dismiss(toastId.current);
      });
    }

    window.addEventListener('offline', () => {
      Logger.info('window offline');
      const id = Toast(
        localization.offlineToastText,
        {
          toastId,
          autoClose: false,
          type: 'error',
        },
      );
      if (id) toastId.current = id;
    });

    window.addEventListener('images:dropped', assetsActions.deselectAll, false);

    window.addEventListener(
      'import:uploading:progress',
      (e) => this.renderToolbarProgress(e.detail),
      false,
    );
    window.addEventListener(
      'import:downloading:progress',
      (e) => this.renderToolbarProgress(e.detail),
      false,
    );
    window.addEventListener(
      'preview:uploading:progress',
      (e) => this.renderToolbarProgress(e.detail),
      false,
    );

    window.addEventListener('toolbar:ui:logout', this.logout, false);
    window.addEventListener('toolbar:ui:tutorials', this.tutorials, false);

    window.addEventListener(
      'toolbar:ui:liveSupport',
      (e) => {
        // to avoid automatic Intercom user creation at app visit
        // instead of this we are creating intercom user when they are open messenger for first time
        // this will save us a bit of bucks
        Logger.log('User', 'LiveChat', utils.getScreenUrl());
        if (picsioConfig.intercom.enabled) {
          this.startIntercomSession(store.getState().user, e.detail);
        } else {
          window.open('mailto:flt8xv4y@incoming.intercom.io', '_blank');
        }
      },
      false,
    );

    window.addEventListener('hardError', (e) => this.onHardError(e.detail.data), false);
    window.addEventListener('softError', (e) => this.onSoftError(e.detail.data), false);
  },

  async handleDownload() {
    await showDownloadDialog(store.getState().assets.selectedItems);
  },

  runHotkeysListeners() {
    window.addEventListener('hotkeys:catalog:commandA', this.cmdAselectedChecker, false);
    window.addEventListener('hotkeys:catalog:commandD', this.handleDownload, false);

    window.addEventListener(
      'hotkeys:catalog:commandP',
      this.setFlagForSelectedImages.bind(this, 'flagged'),
      false,
    );
    window.addEventListener(
      'hotkeys:catalog:commandX',
      this.setFlagForSelectedImages.bind(this, 'rejected'),
      false,
    );
    window.addEventListener(
      'hotkeys:catalog:commandU',
      this.setFlagForSelectedImages.bind(this, 'unflagged'),
      false,
    );

    window.addEventListener(
      'hotkeys:catalog:alt1',
      this.setRatingForSelectedImages.bind(this, 1),
      false,
    );
    window.addEventListener(
      'hotkeys:catalog:alt2',
      this.setRatingForSelectedImages.bind(this, 2),
      false,
    );
    window.addEventListener(
      'hotkeys:catalog:alt3',
      this.setRatingForSelectedImages.bind(this, 3),
      false,
    );
    window.addEventListener(
      'hotkeys:catalog:alt4',
      this.setRatingForSelectedImages.bind(this, 4),
      false,
    );
    window.addEventListener(
      'hotkeys:catalog:alt5',
      this.setRatingForSelectedImages.bind(this, 5),
      false,
    );

    window.addEventListener(
      'hotkeys:preview:alt1',
      this.setRatingForSelectedImages.bind(this, 1),
      false,
    );
    window.addEventListener(
      'hotkeys:preview:alt2',
      this.setRatingForSelectedImages.bind(this, 2),
      false,
    );
    window.addEventListener(
      'hotkeys:preview:alt3',
      this.setRatingForSelectedImages.bind(this, 3),
      false,
    );
    window.addEventListener(
      'hotkeys:preview:alt4',
      this.setRatingForSelectedImages.bind(this, 4),
      false,
    );
    window.addEventListener(
      'hotkeys:preview:alt5',
      this.setRatingForSelectedImages.bind(this, 5),
      false,
    );

    if (ua.browser.family === 'Safari') {
      window.addEventListener(
        'hotkeys:catalog:alt6',
        this.setColorForSelectedImagesByOrder.bind(this, 0),
        false,
      );
      window.addEventListener(
        'hotkeys:catalog:alt7',
        this.setColorForSelectedImagesByOrder.bind(this, 1),
        false,
      );
      window.addEventListener(
        'hotkeys:catalog:alt8',
        this.setColorForSelectedImagesByOrder.bind(this, 2),
        false,
      );
      window.addEventListener(
        'hotkeys:catalog:alt9',
        this.setColorForSelectedImagesByOrder.bind(this, 3),
        false,
      );
      window.addEventListener(
        'hotkeys:catalog:alt0',
        this.setColorForSelectedImages.bind(this, COLOR_LABELS.noColor),
        false,
      );
    } else {
      window.addEventListener(
        'hotkeys:catalog:command6',
        this.setColorForSelectedImagesByOrder.bind(this, 0),
        false,
      );
      window.addEventListener(
        'hotkeys:catalog:command7',
        this.setColorForSelectedImagesByOrder.bind(this, 1),
        false,
      );
      window.addEventListener(
        'hotkeys:catalog:command8',
        this.setColorForSelectedImagesByOrder.bind(this, 2),
        false,
      );
      window.addEventListener(
        'hotkeys:catalog:command9',
        this.setColorForSelectedImagesByOrder.bind(this, 3),
        false,
      );
      window.addEventListener(
        'hotkeys:catalog:command0',
        this.setColorForSelectedImages.bind(this, COLOR_LABELS.noColor),
        false,
      );
    }

    window.addEventListener(
      'hotkeys:catalog:command/',
      () => {
        Logger.log('User', 'SettingsShortcutsBtnClicked');

        return navigate('/shortcuts');
      },
      false,
    );

    window.addEventListener('hotkeys:catalog:commandF', this.focusInputSearch, false);

    window.addEventListener(
      'hotkeys:catalog:commandC',
      () => {
        if (store.getState().main.openedTree === 'collections') {
          mainActions.changeTree('');
        } else {
          mainActions.changeTree('collections', picsioConfig.isProofing);
        }
      },
      false,
    );
    window.addEventListener(
      'hotkeys:catalog:commandM',
      () => {
        mainActions.toggleDetails();
      },
      false,
    );

    window.addEventListener(
      'hotkeys:catalog:commandI',
      () => {
        picsioConfig.isMainApp && mainActions.toggleImport();
      },
      false,
    );

    window.addEventListener(
      'hotkeys:catalog:up',
      () => {
        const container = document.querySelector('.innerCatalog');
        container.scrollTop -= 200;
      },
      false,
    );
    window.addEventListener(
      'hotkeys:catalog:down',
      () => {
        const container = document.querySelector('.innerCatalog');
        container.scrollTop += 200;
      },
      false,
    );

    function scrollCatalogToBeginning() {
      const container = document.querySelector('.innerCatalog');
      container.scrollTop = 0;
    }

    function scrollCatalogToEnd() {
      const container = document.querySelector('.innerCatalog');
      container.scrollTop = container.scrollHeight - window.innerHeight * 2;
    }

    function scrollPageUp() {
      const container = document.querySelector('.innerCatalog');
      container.scrollTop -= container.clientHeight;
    }

    function scrollPadeDown() {
      const container = document.querySelector('.innerCatalog');
      container.scrollTop += container.clientHeight;
    }

    window.addEventListener('hotkeys:catalog:commandUp', scrollCatalogToBeginning, false);
    window.addEventListener('hotkeys:catalog:home', scrollCatalogToBeginning, false);

    window.addEventListener('hotkeys:catalog:commandDown', scrollCatalogToEnd, false);
    window.addEventListener('hotkeys:catalog:end', scrollCatalogToEnd, false);

    window.addEventListener('hotkeys:catalog:altUp', scrollPageUp, false);
    window.addEventListener('hotkeys:catalog:pageup', scrollPageUp, false);

    window.addEventListener('hotkeys:catalog:altDown', scrollPadeDown, false);
    window.addEventListener('hotkeys:catalog:pagedown', scrollPadeDown, false);
  },

  initIntercom() {
    const { user } = store.getState();
    const { chatSupport } = user.subscriptionFeatures;
    const isTrialPlan = user.subscriptionFeatures?.planId === 'trial';
    const isMetadataCookie = utils.getCookie('metadata.utm_campaign') || localStorage.getItem('metadata.utm_campaign') || false;

    if ((isTrialPlan || isUnsubscribed(user)) && isMetadataCookie) {
      return;
    }

    if (chatSupport || isUnsubscribed(user)) {
      window.Intercom('boot', {
        app_id: picsioConfig.intercom.appId,
        custom_launcher_selector: '#itemliveSupport',
      });
      markIntercomEventSessionStarted();
    }

    // We don't want to all users in Intercom, because of money, we lazy create them on webchat lauch.
    // Once user opened Intercom we will autostart Intercom session for they.
    // Other options is to signup only trial and subscribed users, others should be dropped after trial ends.
    if (user && user.intercomUser) {
      this.startIntercomSession(user);
    }

    window.Intercom('onUnreadCountChange', (unreadCount) => {
      Logger.info('Intercom unread count', unreadCount);

      const counter = document.querySelector('.liveSupportCounter');
      if (counter && unreadCount > 0) {
        counter.innerHTML = unreadCount;
      } else if (counter && unreadCount === 0) {
        counter.innerHTML = '';
      }
    });
  },

  initPubsub() {
    const id = (picsioConfig.isMainApp || ua.isMobileApp())
      ? store.getState().user._id // user id
      : picsioConfig.isProofing
        ? picsioConfig.access._id // website id
        : picsioConfig.isSingleApp
          ? picsioConfig.access.assetId // asset id
          : null;

    const teammatesIds = (picsioConfig.isMainApp || ua.isMobileApp())
      ? store.getState().teammates.items.filter(({ _id }) => _id !== id).map(({ _id }) => _id.toString())
      : null;

    PubSubService.init(id, teammatesIds);
    const socketAllowsBrowserNotification = (eventType) => {
      if (!picsioConfig.isMainApp) return true;
      const integrations = store.getState().user.integrations || [];
      const pushSettings = integrations.find((item) => item.type === 'socket' || item.type === 'push');
      return Boolean(
        pushSettings
        && Array.isArray(pushSettings.eventTypes)
        && pushSettings.eventTypes.includes(eventType),
      );
    };

    PubSubService.subscribe('asset.comment.added', (params) => {
      const pushDisabledFor = (params && params.pushDisabledFor) || [];
      const collectionAllowsPush = Array.isArray(params && params.pushDisabledFor)
        && !pushDisabledFor.some((userId) => String(userId) === String(id));
      if (!collectionAllowsPush && !socketAllowsBrowserNotification('asset.comment.added')) return;
      const isUserUnsubscribed = checkUserEventSubscription(
        id,
        params,
        store.getState().user.integrations || [],
        'push',
      );
      if (isUserUnsubscribed) return;
      if (id === (params.initiator && params.initiator._id)) return;
      // now we will hardcode a workaround that will not show notifications from public websites
      if (!picsioConfig.isMainApp && !params.initiator) {
        Logger.info('Comment comes from public. Skip');
        return;
      }

      BrowserNotifications.create({
        title: 'Comment added',
        body: params.data.comment.text,
        onclick() {
          try {
            navigate(`preview/${params.data.asset._id}`);
          } catch (err) {
            Logger.error(new Error('BrowserNotification: `comment added` click failed'), { assetId: params.data.asset._id, error: err }, [
              'BrowserNotificationClickFailed',
              {
                errorMessage: (err && err.message) || 'NoMessage',
              },
            ]);
          }
        },
      });
    });

    PubSubService.subscribe('asset.revision.created', (params) => {
      if (!socketAllowsBrowserNotification('asset.revision.created')) return;
      const isUserUnsubscribed = checkUserEventSubscription(
        id,
        params,
        store.getState().user.integrations || [],
        'push',
      );
      if (isUserUnsubscribed) return;
      if (id === (params.initiator && params.initiator._id)) return;

      BrowserNotifications.create({
        title: 'Revision added',
        body: 'New revision was added to image',
        onclick() {
          try {
            navigate(`preview/${params.data.asset._id}`);
          } catch (err) {
            Logger.error(new Error('BrowserNotification: `revision added` click failed'), { assetId: params.data.asset._id, error: err }, [
              'BrowserNotificationClickFailed',
              {
                errorMessage: (err && err.message) || 'NoMessage',
                assetId: params.data.asset._id,
              },
            ]);
          }
        },
      });
    });
  },

  checkIsBrowserWebpSupported() {
    const callback = (feature, result) => {
      if (result) {
        Logger.info('webp animation supported');
        mainActions.setIsWebpSupported(true);
      } else {
        Logger.info('webp animation not supported');
        mainActions.setIsWebpSupported(false);
      }
    };
    utils.checkWebpFeature('animation', callback);
  },

  /**
   * App should be started by user like in OS. User here should be already authorized.
   * @return {[type]}      [description]
   */
  async startMainApp() {
    try {
      const isSplashScreenAvailable = Capacitor.isPluginAvailable('SplashScreen');
      if (isSplashScreenAvailable) {
        SplashScreen.hide();
      }
      if (!window.navigator.cookieEnabled) {
        // need to move this dialog to separate file
        // takes too much space

        showDialog({
          title: 'Cookies are required',
          text:
            'Cookies aren\'t enabled in your browser. Enable cookies in your browser preferences to continue <br /><br /><a href="#">"Read how to"</a>',
          textBtnCancel: null,
          textBtnOk: null,
          icon: 'warning',
        });

        return;
      }

      checkBrowser();

      /** remove initial spinner from HTML */
      const $initialSpinner = document.getElementById('start-up-spinner');
      if ($initialSpinner) document.body.removeChild($initialSpinner);

      this.spinnerStartApp = UiBlocker.block(localization.SPINNERS.STARTING_APPLICATION);

      initShortcuts('catalog');
      this.runHotkeysListeners();

      this.spinnerStartApp.setTitle('Loading user data...');

      mainActions.setPanelSize();
      try {
        await userActions.getUser();
        const { user } = store.getState();
        const overdueInvoiceToken = user?.modalLastShownAt?.overdueInvoice;
        const prevOverdueInvoiceToken = localStorage.getItem('overdueInvoiceModalToken');

        if (overdueInvoiceToken && overdueInvoiceToken !== prevOverdueInvoiceToken) {
          localStorage.setItem('overdueInvoiceModalToken', overdueInvoiceToken);
          const subscriptionEnd = getSubscriptionEnd(user);
          const dueDate = formatDueDate(subscriptionEnd, 30);

          showDialog({
            title: localization.DIALOGS.OVERDUE_INVOICE.title,
            text: localization.DIALOGS.OVERDUE_INVOICE.text(dueDate),
            textBtnCancel: localization.DIALOGS.OVERDUE_INVOICE.textBtnCancel,
            textBtnOk: localization.DIALOGS.OVERDUE_INVOICE.textBtnOk,
            onOk: () => {
              if (isHaveTeammatePermission('manageBilling', store.getState().user)) {
                navigate('/billing?tab=invoices');

                return;
              }
              const teammates = store.getState().teammates.items;
              const roles = store.getState().roles.items;
              const adminRoles = utils.getTeamManagers(user, teammates, roles);
              const adminRolesEmails = adminRoles.slice(0, 5).map((role) => (
                <>
                  <Tag
                    key={role._id}
                    type="user"
                    avatar={role.avatar}
                    text={role.email}
                  />
                </>
              ));

              showDialog({
                title: localization.PROMOTIONAL_BLOCK.title,
                text: localization.PROMOTIONAL_BLOCK.text(adminRolesEmails),
                textBtnOk: localization.DIALOGS.LOGOUT_DIALOG.OK_TEXT,
                textBtnCancel: null,
              });
            },
          });
        }
      } catch (err) {
        const connection = utils.getNavigatorConnectionInfo();
        Logger.error(new Error('Can not load user'), { error: err }, [
          'UserLoadFailed',
          { errorMessage: (err && err.message) || 'NoMessage', connection },
        ]);
        throw new GetDataError('Can not load user', 'getUserFailed', err);
      }

      /**
       * PCS-11271: once the Toriut trial has expired, the server hard-locks every
       * JSON/XHR call below this point (roles, team, collections, saved searches,
       * custom fields, keywords, notifications, ...) - see
       * `server/app/middlewares/ensureToriutTrialAccess.js`. Awaiting any of those
       * and having them 403 would throw a `GetDataError` (see the `getRoles`/
       * `getTeam`/`getCollections` try/catches just below), which `index.js`'s
       * `loadApp()` catch-all treats as a generic "app start failed" error and
       * NEVER mounts the React tree at all - so `<ToriutTrialFlow>` (rendered by
       * `router.js` once the tree *is* mounted) would never even get a chance to
       * show. Skip straight to letting `index.js` mount React instead; the
       * already-fetched `user.toriutAccessState` is all `router.js` needs to
       * render the trial-expired flow.
       */
      if (isToriutTrialLocked(store.getState().user)) {
        this.spinnerStartApp.destroy();
        return;
      }

      try {
        await rolesActions.getRoles();
        await teamActions.getTeam();
      } catch (err) {
        Logger.error(new Error('Can not load user data'), { error: err }, [
          'UserDataLoadFailed',
          { errorMessage: (err && err.message) || 'NoMessage' },
        ]);
        UiBlocker.unblock();
        throw new GetDataError('Can not load user data', 'getUserDataFailed', err);
      }

      /** ****** start async functions ******* */
      try {
        await collectionsActions.getCollections();
        if (picsioConfig.isPim) productsActions.getProducts();
      } catch (err) {
        Logger.error(new Error('Can not load collections data'), { error: err }, [
          'CollectionsDataLoadFailed',
          { errorMessage: (err && err.message) || 'NoMessage' },
        ]);
        throw new GetDataError('Can not load collections data', 'getCollectionsFailed', err);
      }

      const defaultCollectionsToFetch = [
        savedSearchesActions.getSavedSearches(),
        getCustomFieldsAction(),
      ];

      if (picsioConfig.isPim) {
        defaultCollectionsToFetch.push(getProductAttributesAction());
      }

      if (isHaveTeammatePermission('manageLightboards', store.getState().user) && !picsioConfig.isPim) {
        defaultCollectionsToFetch.push(lightboardsActions.getLightboards());
      }

      if (isHaveTeammatePermission('manageInboxes', store.getState().user)) {
        const { inboxes } = store.getState().user.subscriptionFeatures || {};
        if (inboxes) defaultCollectionsToFetch.push(inboxesActions.getInboxes());
      }

      const { subscriptionFeatures, isAccountOverdue = false } = store.getState().user;

      this.spinnerStartApp.setTitle(localization.SPINNERS.LOADING_LIBRARY);
      try {
        await Q.all(defaultCollectionsToFetch);
      } catch (err) {
        throw new GetDataError('Can not load default collections', 'getDefaultCollectionsFailed', err);
      }

      if (subscriptionFeatures.faceRecognition && !picsioConfig.isPim) facesActions.getFaces();
      keywordsActions.search();
      if (!picsioConfig.isPim) assetsActions.getWatermarks();

      /** ****** end async functions ******* */

      // detect user date locale and set it to dayjs
      setUserDateLocale();

      this.delegateGlobalEvents();

      notificationsActions.getNotifications();
      notificationsActions.fetchJobsStatus();

      this.initIntercom();
      this.initPubsub();
      this.setSiteTitle();

      // check if we need to redirect user to billing page
      if (isAccountOverdue) {
        if (window.location.pathname !== '/billing') {
          replace('/billing?tab=invoices');
        }
        this.spinnerStartApp.destroy();
        return;
      }

      // check subscription
      if (!subscriptionFeatures.access) {
        if (window.location.pathname !== '/billing') {
          replace('/billing?tab=overview');
        }
        this.spinnerStartApp.destroy();
        return;
      }

      this.spinnerStartApp.setTitle(localization.SPINNERS.CONNECTING_STORAGES(store.getState().user.storageName));

      // Get and set status of recursive search from cookie when app starts not from catalog view, e.g Billing, myAccount
      if (utils.LocalStorage.get('picsio.recursiveSearch') === false) {
        const { lightboardId, collectionIds, keywords } = store.getState().router.location.query;
        if (!lightboardId && !keywords && !collectionIds) {
          collectionsActions.setRecursiveSearch(true);
        }
      }

      utils.setCookie('picsio.user_id', store.getState().user._id);

      Logger.log('UI', 'AppStarted', utils.getScreenUrl());
      if (ua.isPWA()) {
        Logger.log('User', 'PWAAppStarted');
      }

      if (picsioConfig.isPim) {
        const { user } = store.getState();
        const isSetWizInitialSyncCompleted = utils.LocalStorage.get('toriut.setWizInitialSyncCompleted');

        if (
          user.initialProductsPulling === 'complete'
          && (user.productsPulling === 'waiting' || user.productsPulling === 'running')
          && !isSetWizInitialSyncCompleted
        ) {
          sendEventToIntercom('SetWizInitialSyncCompleted');
          utils.LocalStorage.set('toriut.setWizInitialSyncCompleted', true);
        }
      }

      if (ua.isMobileApp()) {
        const userPlatform = ua.getPlatform();
        const deviceInfo = await Device.getInfo() || {};
        Logger.log(
          'User',
          `${userPlatform ? utils.capitalizeFirstLetter(userPlatform) : 'Mobile'}AppStarted`,
          {
            ...deviceInfo,
          },
        );
        App.addListener('appStateChange', (state) => {
          Logger.log('User', `MobileApp${state.isActive ? 'Active' : 'Inactive'}`);
        });

        document.addEventListener(
          'deviceready',
          () => {
            if (window.cordova) {
              // cordova.plugins.notification.badge is now available
              window.cordova.plugins?.notification?.badge?.hasPermission((granted) => {
                Logger.info(`Notification badge hasPermission: [${granted}]`);
                if (!granted) {
                  window.cordova.plugins?.notification?.badge?.requestPermission((result) => {
                    Logger.info(`Notification badge requestPermission: [${result}]`);
                  });
                }
              });
            }
          },
          false,
        );

        if (Capacitor.getPlatform() === 'android') {
          await EdgeToEdge.enable();
        }

        /** Init mobile app notifications */
        initMobileNotifications();

        // Check 2FA for MobileApp user
        const { user } = store.getState();
        const { teamTwoFactorMode, twoFactorConfigured } = user;
        const twoFactorEnabled = isTwoFactorEnabled(teamTwoFactorMode);
        if (twoFactorEnabled && !twoFactorConfigured) {
          Logger.log('UI', 'MobileRedirectUserToSecurity', { twoFactorEnabled, twoFactorConfigured });
          navigate('/users/me?tab=security');
        }
      }

      window.addEventListener('beforeunload', () => {
        Logger.log('User', 'AppRefreshed', utils.getScreenUrl());
      });

      const oldDownloadableItems = utils.LocalStorage.get('picsioDownloadingProgress');
      if (oldDownloadableItems && oldDownloadableItems.length) {
        const names = oldDownloadableItems.map((n) => n.name).slice(0, 3);
        const rest = oldDownloadableItems.length - 3;
        const more = rest > 0 ? ` and (${rest}) more` : '';

        showDialog({
          title: 'Resume downloading',
          text: `Recently you have started downloading the following assets: ${names.join(
            ', ',
          )} ${more}`,
          textBtnOk: 'Resume',
          textBtnCancel: 'Discard',
          onOk() {
            utils.LocalStorage.remove('picsioDownloadingProgress');
            downloadListActions.addToDownloadList(oldDownloadableItems);
          },
          onCancel() {
            utils.LocalStorage.remove('picsioDownloadingProgress');
          },
          onClose() { }, // empty function - because we should just remove dialog, and show it again during next starting app
        });
      }

      this.checkIsBrowserWebpSupported();

      UiBlocker.unblock();
    } catch (err) {
      UiBlocker.unblock();
      throw err;
    }
  },

  async startProofingApp() {
    try {
      document.addEventListener('contextmenu', (e) => e.preventDefault(), false);
      Logger.setUser({ displayName: 'Web user', email: picsioConfig.access.alias });

      /** remove initial spinner from HTML */
      const $initialSpinner = document.getElementById('start-up-spinner');
      if ($initialSpinner) document.body.removeChild($initialSpinner);

      this.spinnerStartApp = UiBlocker.block();

      checkBrowser();

      /** ****** start async functions ******* */
      await getCustomFieldsAction();
      if (picsioConfig.access.tagsTreeShow) await collectionsActions.getCollections();
      /** ****** end async functions ******* */

      initShortcuts('catalog');
      this.delegateGlobalEvents();
      this.initPubsub();
      setUserDateLocale();
      utils.preventPullToRefresh('.wrapperPicsioApp');
      Logger.log('UI', 'ProofingStarted', utils.getScreenUrl());
      window.addEventListener('beforeunload', () => {
        Logger.log('User', 'ProofingRefreshed', utils.getScreenUrl());
      });
      const isConsentNeeded = window.websiteConfig.visitingConsentEnable;
      if (isConsentNeeded && !utils.getCookie(utils.getWebsiteCookieName('visiting'))) {
        Logger.log('UI', 'VisitingConsentDialog');
        showDialog({
          title: localization.DIALOGS.DOWNLOAD_CONSENT.TITLE(
            window.websiteConfig.visitingConsentTitle,
          ),
          text: localization.DIALOGS.DOWNLOAD_CONSENT.TEXT(
            window.websiteConfig.visitingConsentMessage,
          ),
          textBtnCancel: localization.DIALOGS.DOWNLOAD_CONSENT.CANCEL_TEXT,
          textBtnOk: localization.DIALOGS.DOWNLOAD_CONSENT.OK_TEXT,
          onOk: () => {
            Logger.log('User', 'VisitingConsentDialogConfirm');
          },
          onCancel: () => {
            Logger.log('User', 'VisitingConsentDialogReject');
            const consentUrl = utils.prepareUrlForConsent(window.websiteConfig.alias);
            window.open(consentUrl, '_self');
          },
        });
      }

      this.checkIsBrowserWebpSupported();

      this.spinnerStartApp.destroy();
    } catch (err) {
      const text = localization.APP_START.DEFAULT_ERROR;
      UiBlocker.unblock();
      showDialog({
        title: 'Error',
        text,
        textBtnOk: null,
        textBtnCancel: null,
      });
      Logger.error(new Error('App start failed'), { error: err }, [
        'StartProofingFailed',
        { errorMessage: (err && err.message) || 'NoMessage' },
      ]);
    }
  },

  async startSingleApp() {
    try {
      document.addEventListener('contextmenu', (e) => e.preventDefault(), false);
      Logger.setUser({ displayName: 'Web user', email: picsioConfig.access.alias });

      /** remove initial spinner from HTML */
      const $initialSpinner = document.getElementById('start-up-spinner');
      if ($initialSpinner) document.body.removeChild($initialSpinner);

      this.spinnerStartApp = UiBlocker.block();

      checkBrowser();

      /** ****** start async functions ******* */
      await getCustomFieldsAction();
      /** ****** end async functions ******* */

      this.delegateGlobalEvents();
      this.initPubsub();
      setUserDateLocale();
      Logger.log('UI', 'SasStarted', utils.getScreenUrl());
      window.addEventListener('beforeunload', () => {
        Logger.log('User', 'SasRefreshed', utils.getScreenUrl());
      });

      const isConsentNeeded = window.websiteConfig.visitingConsentEnable;
      if (isConsentNeeded && !utils.getCookie(utils.getWebsiteCookieName('visiting'))) {
        Logger.log('UI', 'VisitingConsentDialog');
        showDialog({
          title: localization.DIALOGS.DOWNLOAD_CONSENT.TITLE(
            window.websiteConfig.visitingConsentTitle,
          ),
          text: localization.DIALOGS.DOWNLOAD_CONSENT.TEXT(
            window.websiteConfig.visitingConsentMessage,
          ),
          textBtnCancel: localization.DIALOGS.DOWNLOAD_CONSENT.CANCEL_TEXT,
          textBtnOk: localization.DIALOGS.DOWNLOAD_CONSENT.OK_TEXT,
          onOk: () => {
            Logger.log('User', 'VisitingConsentDialogConfirm');
          },
          onCancel: () => {
            Logger.log('User', 'VisitingConsentDialogReject');
            const consentUrl = utils.prepareUrlForConsent(window.websiteConfig.alias);
            window.open(consentUrl, '_self');
          },
        });
      }

      this.spinnerStartApp.destroy();
    } catch (err) {
      const text = localization.APP_START.DEFAULT_ERROR;
      UiBlocker.unblock();
      showDialog({
        title: 'Error',
        text,
        textBtnOk: null,
        textBtnCancel: null,
      });

      Logger.error(new Error('App start failed'), { error: err }, [
        'StartSingleSharingFailed',
        {
          errorMessage: (err && err.message) || 'NoMessage',
        },
      ]);
    }
  },

  async startInboxApp() {
    try {
      Logger.setUser({ displayName: 'Web user', email: picsioConfig.access.alias });

      /** remove initial spinner from HTML */
      const $initialSpinner = document.getElementById('start-up-spinner');
      if ($initialSpinner) document.body.removeChild($initialSpinner);

      this.spinnerStartApp = UiBlocker.block();

      checkBrowser();

      Logger.log('UI', 'InboxStarted', utils.getScreenUrl());
      window.addEventListener('beforeunload', () => {
        Logger.log('User', 'InboxRefreshed', utils.getScreenUrl());
      });

      const isConsentNeeded = window.inbox.visitingConsentEnable;
      if (isConsentNeeded) {
        Logger.log('UI', 'VisitingConsentDialog');
        showDialog({
          title: localization.DIALOGS.DOWNLOAD_CONSENT.TITLE(window.inbox.visitingConsentTitle),
          text: localization.DIALOGS.DOWNLOAD_CONSENT.TEXT(window.inbox.visitingConsentMessage),
          textBtnCancel: localization.DIALOGS.DOWNLOAD_CONSENT.CANCEL_TEXT,
          textBtnOk: localization.DIALOGS.DOWNLOAD_CONSENT.OK_TEXT,
          onOk: () => {
            Logger.log('User', 'VisitingConsentDialogConfirm');
          },
          onCancel: () => {
            Logger.log('User', 'VisitingConsentDialogReject');
            const consentUrl = utils.prepareUrlForConsent(window.inbox.alias);
            window.open(consentUrl, '_self');
          },
        });
      }

      this.spinnerStartApp.destroy();
    } catch (err) {
      const text = localization.APP_START.DEFAULT_ERROR;
      UiBlocker.unblock();
      showDialog({
        title: 'Error',
        text,
        textBtnOk: null,
        textBtnCancel: null,
      });
      Logger.error(new Error('Inbox start failed'), { error: err }, [
        'StartInboxFailed',
        { errorMessage: (err && err.message) || 'NoMessage' },
      ]);
    }
  },

  focusInputSearch() {
    const searchInput = document.querySelector('.searchHeader input[type="text"]');
    if (searchInput) searchInput.focus();
  },

  tutorials() {
    Logger.log('Catalog', 'GetTutorial');
    if (picsioConfig.isPim) {
      window.open(CONSTANTS.support.HELP_CENTER_TORIUT_URL, '_blank');
    } else {
      window.open(CONSTANTS.support.HELP_CENTER_URL, '_blank');
    }
  },

  cmdAselectedChecker() {
    /** Handle CMD + A in the import panel */
    if (store.getState().main.importOpened) return;
    /** Handle CMD + A for catalog view */
    Logger.log('User', 'KeyboardSelectAll');

    if (store.getState().router.location.search.includes('isMediaMatch') && picsioConfig.isPim) {
      assetsActions.selectAllByMatch();
    } else {
      assetsActions.selectAll();
    }
    // on call about clipboard we've decided that cmd+a will only select all
  },

  setFlagForSelectedImages(value) {
    assetsActions.changeFlag(store.getState().assets.selectedItems, value);
  },

  setRatingForSelectedImages(value) {
    const state = store.getState();
    const { selectedItems } = state.assets;
    const previewAssetId = getPreviewId();

    const assetsIds = isRoutePreview() && previewAssetId ? [previewAssetId] : selectedItems;

    assetsActions.changeRating(assetsIds, value);
  },

  setColorForSelectedImages(value) {
    assetsActions.changeColor(store.getState().assets.selectedItems, value);
  },

  /** Hotkeys follow the visual order of the configured labels, not fixed slugs. */
  setColorForSelectedImagesByOrder(order) {
    const label = selectColorLabels(store.getState())[order];

    if (label) this.setColorForSelectedImages(label.slug);
  },

  renderToolbarProgress(data) {
    const ElParent = document.querySelector(data.ElParent);
    const progressWrapper = document.querySelector(`${data.ElParent} .pProgressWrapper`);

    if (progressWrapper) {
      ElParent.removeChild(progressWrapper);
    }

    if (!ElParent || data.percantage === 0 || data.percantage === 100) {
      return;
    }

    // progressBar contains from 2 semicircles, when percantage more then 50 we fix first semicircle, and move second
    const template = [
      '<div class="pProgressWrapper">',
      `<div class="pProgressAnimation ${data.percantage > 50 ? 'alreadyCenter' : ''}">`,
      `<div class="pProgressAnimatedElem" style="transform: rotate(${(data.percantage * 360) / 100
      }deg);">`,
      '<div class="pProgressAnimatedInner"></div>',
      '</div>',
      data.percantage > 50
        ? '<div class="pProgressConstantElem" style="transform: rotate(180deg); position: absolute; top: 0;"><div class="pProgressAnimatedInner"></div></div>'
        : '',
      '</div>',
      '</div>',
    ].join('');
    ElParent.insertAdjacentHTML('afterbegin', template);
  },

  // this handler is needed to handle errors which break down application
  // so we should disallow users to do anything except reload application
  onHardError(data) {
    showDialog({
      title: 'Error',
      text: data.message,
      textBtnOk: data.btnTxt || 'Ok',
      textBtnCancel: null,
      onOk: reloadApp,
      onCancel() { },
    });
  },

  // this handler is needed to handle errors which DON'T break down application
  // so we should just show informational popup
  onSoftError(data) {
    showDialog({
      title: 'Error',
      text: data.message,
      textBtnCancel: null,
      onOk() { },
      onCancel() { },
    });
  },

  setSiteTitle() {
    const userName = store.getState().user.displayName;
    if (userName) {
      document.title = `${picsioConfig.isPim ? 'Toriut.com' : 'Pics.io'} - ${userName}`;
    }
  },
};

window.app = app;

// export default hot(app);
export default app;
